import { apiFetch } from './api';

const DEFAULT_ORDER = [
  'kpi-summary',
  'layout-nav',
  'clock',
  'analog-clock',
  'tickers',
  'upcoming-events',
  'weather',
  'hourly-weather',
  'traffic',
  'news',
  'shopping',
  'countdowns',
  'goals',
  'goals-chart',
  'calendar-month',
  'airbnb-alerts',
  'airbnb',
  'airbnb-calendar',
  'airbnb-earnings',
  'airbnb-ytd',
  'goals-history',
  'quote',
  'receipts',
  'receipt-emails',
  'email-browser',
  'receipt-records',
  'receipt-exporter',
  'packages',
  'wedding-guests',
  'wedding-dashboard',
  'web-embed',
  'simplefin',
  'account-balances',
  'transactions',
  'net-worth-chart',
  'financial-dashboard',
  'transaction-reports',
  'category-rules',
  'global-search',
  'data-export',
  'portfolio',
  'culvers-funding',
  'culvers-future',
  'notifications',
  'backup-admin',
  'email-overview',
  'category-config',
  'background-processes',
  'monthly-budget',
  'stats',
  'account-snapshots',
];

export const PANEL_GROUPS = {
  'Overview': ['kpi-summary', 'stats', 'layout-nav'],
  'Time': ['clock', 'analog-clock'],
  'Calendar': ['upcoming-events', 'calendar-month'],
  'Weather & Traffic': ['weather', 'hourly-weather', 'traffic'],
  'Markets & News': ['tickers', 'news', 'quote'],
  'Financial': ['simplefin', 'account-balances', 'account-snapshots', 'transactions', 'net-worth-chart', 'financial-dashboard', 'transaction-reports', 'category-rules', 'category-config', 'portfolio', 'monthly-budget', 'culvers-funding', 'culvers-future', 'global-search', 'data-export'],
  'Airbnb': ['airbnb-alerts', 'airbnb', 'airbnb-calendar', 'airbnb-earnings', 'airbnb-ytd'],
  'Goals': ['goals', 'goals-chart', 'goals-history'],
  'Receipts & Packages': ['receipts', 'email-browser', 'email-overview', 'receipt-emails', 'receipt-records', 'receipt-exporter', 'packages'],
  'Shopping & Lists': ['shopping', 'countdowns'],
  'Wedding': ['wedding-guests', 'wedding-dashboard'],
  'Notifications': ['notifications'],
  'Admin': ['backup-admin', 'background-processes'],
  'Other': ['web-embed'],
};

export const PANEL_NAMES = {
  'kpi-summary': 'At a Glance',
  'layout-nav': 'Layout Navigator',
  'clock': 'Digital Clock',
  'analog-clock': 'Analog Clock',
  'upcoming-events': 'Upcoming Events',
  'calendar-month': 'Calendar',
  'weather': 'Weather',
  'hourly-weather': 'Hourly Forecast',
  'traffic': 'Commute to Work',
  'tickers': 'Markets',
  'news': 'News',
  'shopping': 'Shopping List',
  'countdowns': 'Countdowns',
  'airbnb-alerts': 'Airbnb Alerts',
  'airbnb': 'Airbnb Reservations',
  'airbnb-calendar': 'Airbnb Calendar',
  'airbnb-earnings': 'Airbnb Earnings',
  'airbnb-ytd': 'YTD Airbnb Earnings',
  'goals': 'Goals',
  'goals-chart': 'Goals Tracker',
  'goals-history': 'Goals History',
  'quote': 'Quote of the Day',
  'receipts': 'Email Importer',
  'receipt-emails': 'Email Receipt Importer',
  'email-browser': 'Email Browser',
  'receipt-records': 'Receipts',
  'receipt-exporter': 'Receipt Exporter',
  'packages': 'Package Tracking',
  'wedding-guests': 'Wedding Guest Tracker',
  'wedding-dashboard': 'Wedding Dashboard',
  'web-embed': 'Web Embed',
  'simplefin': 'SimpleFIN',
  'account-balances': 'Account Balances',
  'transactions': 'Transactions',
  'net-worth-chart': 'Net Worth Trend',
  'financial-dashboard': 'Financial Overview',
  'transaction-reports': 'Transaction Reports',
  'category-rules': 'Category Rules',
  'global-search': 'Search Everything',
  'data-export': 'Data Export',
  'portfolio': 'Portfolio',
  'culvers-funding': "Culver's Funding",
  'culvers-future': "Culver's Funding (Future) - End Of Year",
  'notifications': 'Text Notifications',
  'backup-admin': 'Backup Admin',
  'email-overview': 'Email Overview',
  'category-config': 'Category Configuration',
  'background-processes': 'Background Processes',
  'monthly-budget': 'Monthly Gains vs Expenses',
  'stats': 'Stats',
  'account-snapshots': 'Historic Account Balances',
};

function ensureAllPanels(order, hidden) {
  const missing = DEFAULT_ORDER.filter((id) => !order.includes(id));
  if (missing.length > 0) {
    return {
      order: [...order, ...missing],
      hidden: [...(hidden || []), ...missing],
    };
  }
  return { order, hidden: hidden || [] };
}

function createDefaultLayout(name = 'Default') {
  return {
    name,
    order: [...DEFAULT_ORDER],
    hidden: [],
    columns: 2,
  };
}

export async function loadLayoutsFromServer() {
  try {
    const res = await apiFetch('/api/layouts');
    if (!res.ok) throw new Error('Failed to load layouts');
    const data = await res.json();

    if (data.layouts && data.layouts.length > 0) {
      const layouts = data.layouts.map((l) => {
        const { order, hidden } = ensureAllPanels(l.order || [], l.hidden);
        return { ...l, order, hidden };
      });
      return { layouts, currentIndex: data.currentIndex || 0, version: data.version || 0, lastUpdated: data.lastUpdated || null };
    }

    // Server has empty layouts — only migrate if no version exists (truly first time)
    if (data.version > 0) {
      console.warn('Server returned empty layouts with version > 0, using default without overwriting server');
      return { layouts: [createDefaultLayout()], currentIndex: 0, version: data.version, lastUpdated: data.lastUpdated || null };
    }
  } catch (err) {
    console.error('Failed to load layouts from server:', err);
    // On network error, return a default layout WITHOUT saving to server
    return { layouts: [createDefaultLayout()], currentIndex: 0, version: 0, lastUpdated: null };
  }

  // Only reach here on truly empty server (version 0, no layouts) — safe to migrate
  const migrated = migrateFromLocalStorage();
  try {
    await saveLayoutsToServer(migrated.layouts, migrated.currentIndex);
  } catch {}
  return migrated;
}

function migrateFromLocalStorage() {
  const layout = createDefaultLayout();

  try {
    const oldLayouts = localStorage.getItem('meware_layouts');
    if (oldLayouts) {
      const parsed = JSON.parse(oldLayouts);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const layouts = parsed.map((l) => {
          const { order, hidden } = ensureAllPanels(l.order || [], l.hidden);
          return { ...l, order, hidden };
        });
        const idx = parseInt(localStorage.getItem('meware_current_layout')) || 0;
        // Clean up old keys
        localStorage.removeItem('meware_layouts');
        localStorage.removeItem('meware_current_layout');
        return { layouts, currentIndex: idx < layouts.length ? idx : 0 };
      }
    }
  } catch {}

  try {
    const oldOrder = localStorage.getItem('meware_panel_order');
    if (oldOrder) {
      const result = ensureAllPanels(JSON.parse(oldOrder), layout.hidden);
      layout.order = result.order;
      layout.hidden = result.hidden;
    }
    const oldHidden = localStorage.getItem('meware_hidden_panels');
    if (oldHidden) layout.hidden = JSON.parse(oldHidden);
    const oldCols = localStorage.getItem('meware_columns');
    if (oldCols) layout.columns = parseInt(oldCols) || 2;

    localStorage.removeItem('meware_panel_order');
    localStorage.removeItem('meware_hidden_panels');
    localStorage.removeItem('meware_columns');
  } catch {}

  return { layouts: [layout], currentIndex: 0 };
}

export async function saveLayoutsToServer(layouts, currentIndex, version = 0, lastUpdated = null) {
  try {
    const res = await apiFetch('/api/layouts', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ layouts, currentIndex, version, lastUpdated }),
    });
    if (res.status === 409) {
      // Stale version — server has newer data
      const data = await res.json();
      return { conflict: true, serverData: data.layouts, serverLastUpdated: data.serverLastUpdated };
    }
    if (res.ok) {
      const data = await res.json();
      return { conflict: false, version: data.version, lastUpdated: data.lastUpdated };
    }
  } catch (err) {
    console.error('Failed to save layouts:', err);
  }
  return { conflict: false };
}

export function isMobileDevice() {
  return /mobile|android|iphone|ipad/i.test(navigator.userAgent);
}
